import React from "react";
import { Users, Gift, Download, CheckCircle2, XCircle, DollarSign, Clock, Search, Trash2 } from "lucide-react";
import { usd, CAMP_DAY_LABELS } from "../../lib/api";
import { Stat, PageTitle, DAYS, regStatusCls, LastAdminAction, fmtTs } from "./adminUtils";

const th = "text-left px-4 py-3";
const td = "px-4 py-3";

const DayTable = ({ k, data }) => (
  <div className="rounded-2xl bg-white border border-[#E2EBF0] p-5">
    <div className="font-bold text-[#03281B] capitalize mb-3">{k === "football" ? "soccer" : k} by day</div>
    {DAYS.map((d) => (
      <div key={d} className="flex justify-between text-sm py-1 border-b border-slate-50 last:border-0"><span className="text-slate-500">{CAMP_DAY_LABELS[d]}</span><span className="font-bold text-[#008057]">{data[d]}</span></div>
    ))}
  </div>
);

export const OverviewTab = ({ stats }) => (
  <div>
    <PageTitle>Overview</PageTitle>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
      <Stat icon={Users} label="Total Registrations" value={stats.total_registrations} />
      <Stat icon={CheckCircle2} label="Confirmed" value={stats.confirmed} />
      <Stat icon={Clock} label="Pending Payment" value={stats.pending} tone="amber" />
      <Stat icon={Gift} label="Jerseys Remaining" value={`${stats.gifts.remaining}/${stats.gifts.total}`} tone="gold" />
      <Stat icon={DollarSign} label="Confirmed Revenue" value={usd(stats.revenue)} />
      <Stat icon={DollarSign} label="Outstanding" value={usd(stats.outstanding)} tone="amber" />
      <Stat icon={XCircle} label="Cancelled" value={stats.cancelled} tone="red" />
      <Stat icon={Gift} label="Jerseys Awarded" value={stats.gifts.allocated} tone="gold" />
    </div>
    <div className="grid lg:grid-cols-3 gap-4 mt-6">
      {["football", "goalkeeper", "meals"].map((k) => <DayTable key={k} k={k} data={stats.sessions_by_day[k]} />)}
    </div>
  </div>
);

const RegRow = ({ o, onView, onDelete }) => (
  <tr className="border-t border-slate-100 hover:bg-slate-50">
    <td className={`${td} font-mono font-semibold text-[#03281B]`}>{o.reference}</td>
    <td className={td}>{o.guardian.first_name} {o.guardian.last_name}<div className="text-xs text-slate-400">{o.guardian.email}</div></td>
    <td className={`${td} hidden md:table-cell`}>{o.participants.length}</td>
    <td className={`${td} font-semibold`}>{usd(o.total)}</td>
    <td className={td}><span className={`text-xs font-bold px-2 py-1 rounded-full ${regStatusCls(o.registration_status)}`}>{o.registration_status.replace("_", " ")}</span></td>
    <td className={`${td} hidden lg:table-cell`} data-testid={`last-action-${o.reference}`}><LastAdminAction order={o} /></td>
    <td className={`${td} text-right`}><div className="flex items-center justify-end gap-3">
      <button data-testid={`view-reg-${o.reference}`} onClick={() => onView(o)} className="text-[#008057] font-semibold hover:underline">View</button>
      <button data-testid={`delete-reg-${o.reference}`} onClick={() => onDelete(o.reference)} title="Delete registration" className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button>
    </div></td>
  </tr>
);

const RegFilters = ({ q, setQ, statusFilter, setStatusFilter, onSearch }) => (
  <div className="flex flex-wrap gap-3 mt-4">
    <div className="relative flex-1 min-w-[240px]">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onSearch()} placeholder="Search name, email, or reference" className="w-full rounded-xl border-2 border-[#E2EBF0] pl-9 pr-3 py-2.5 text-sm focus:border-[#008057] outline-none" />
    </div>
    <select data-testid="admin-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-xl border-2 border-[#E2EBF0] px-3 py-2.5 text-sm">
      <option value="">All statuses</option><option value="pending_payment">Pending</option><option value="confirmed">Confirmed</option><option value="cancelled">Cancelled</option><option value="annulled">Annulled</option>
    </select>
    <button data-testid="admin-search-btn" onClick={onSearch} className="rounded-xl bg-[#008057] text-white px-5 py-2.5 font-bold text-sm">Search</button>
  </div>
);

export const RegistrationsTab = ({ regs, filters, onView, onDelete }) => (
  <div>
    <PageTitle>Registrations</PageTitle>
    <RegFilters {...filters} />
    <div className="mt-4 rounded-2xl bg-white border border-[#E2EBF0] overflow-x-auto">
      <table data-testid="admin-dashboard-table" className="w-full text-sm">
        <thead className="bg-[#F2F8F5] text-slate-600"><tr>
          <th className={`${th} font-semibold`}>Reference</th><th className={`${th} font-semibold`}>Guardian</th><th className={`${th} font-semibold hidden md:table-cell`}>Participants</th><th className={`${th} font-semibold`}>Total</th><th className={`${th} font-semibold`}>Status</th><th className={`${th} font-semibold hidden lg:table-cell`}>Last Action</th><th className="px-4 py-3"></th>
        </tr></thead>
        <tbody>
          {regs.map((o) => <RegRow key={o.id} o={o} onView={onView} onDelete={onDelete} />)}
          {regs.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No registrations found.</td></tr>}
        </tbody>
      </table>
    </div>
  </div>
);

export const GiftsTab = ({ gifts }) => (
  <div>
    <PageTitle>Jersey Gifts</PageTitle>
    <div className="grid grid-cols-3 gap-4 mt-6 max-w-lg">
      <Stat icon={Gift} label="Total" value={gifts.status.total} tone="gold" />
      <Stat icon={Gift} label="Awarded" value={gifts.status.allocated} tone="gold" />
      <Stat icon={Gift} label="Remaining" value={gifts.status.remaining} tone="gold" />
    </div>
    <div className="mt-6 rounded-2xl bg-white border border-[#E2EBF0] overflow-x-auto">
      <table className="w-full text-sm"><thead className="bg-[#F2F8F5] text-slate-600"><tr><th className={th}>#</th><th className={th}>Reference</th><th className={th}>Participant</th><th className={th}>Size</th><th className={th}>Confirmed</th></tr></thead>
        <tbody>{gifts.allocations.map((g) => (<tr key={g.reference} className="border-t border-slate-100"><td className={`${td} font-bold`}>{g.allocation_number}</td><td className={`${td} font-mono`}>{g.reference}</td><td className={td}>{g.participant_name}</td><td className={td}>{g.jersey_size || <span className="text-slate-400">Pending</span>}</td><td className={`${td} text-xs text-slate-400`}>{g.confirmed_at?.slice(0, 10)}</td></tr>))}
          {gifts.allocations.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No jerseys awarded yet.</td></tr>}</tbody></table>
    </div>
  </div>
);

const EXPORTS = [["registrations", "Registrations CSV", "All registrations with guardian & totals"], ["sizes", "Clothing Sizes CSV", "Kit sizes per participant for equipment prep"], ["services", "Service Bookings CSV", "Additional services per participant"]];

export const ExportsTab = ({ onExport }) => (
  <div>
    <PageTitle>Exports</PageTitle>
    <div className="grid sm:grid-cols-3 gap-4 mt-6">
      {EXPORTS.map(([kind, label, desc]) => (
        <button key={kind} data-testid={`export-${kind}`} onClick={() => onExport(kind)} className="rounded-2xl bg-white border border-[#E2EBF0] p-6 text-left hover:border-[#008057] transition-colors">
          <Download className="text-[#008057]" /><div className="font-bold text-[#03281B] mt-3">{label}</div><div className="text-sm text-slate-500 mt-1">{desc}</div>
        </button>
      ))}
    </div>
  </div>
);

export const AuditTab = ({ audit }) => (
  <div>
    <PageTitle>Audit Log</PageTitle>
    <div className="mt-6 rounded-2xl bg-white border border-[#E2EBF0] overflow-x-auto">
      <table className="w-full text-sm"><thead className="bg-[#F2F8F5] text-slate-600"><tr><th className={th}>Time</th><th className={th}>Action</th><th className={th}>Actor</th><th className={th}>Reference</th><th className={th}>Details</th></tr></thead>
        <tbody>{audit.map((l) => (<tr key={`${l.timestamp}-${l.action}-${l.reference}-${l.actor}`} className="border-t border-slate-100"><td className={`${td} text-xs text-slate-400`}>{fmtTs(l.timestamp, 19)}</td><td className={`${td} font-semibold`}>{l.action}</td><td className={td}>{l.actor}</td><td className={`${td} font-mono`}>{l.reference}</td><td className={`${td} text-slate-500`}>{l.details}</td></tr>))}
          {audit.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No audit entries yet.</td></tr>}</tbody></table>
    </div>
  </div>
);
